import { useAuth } from '@/context/AuthContext';
import { useLeftSidebar } from '@/hooks/useLeftSidebar';

import { cn } from '@/utils/utils';
import { ChevronsLeft } from 'lucide-react';
import { Button } from '../Button/Button';
import { Icon } from '../ui/Icon';
import AccountMenu from './AccountMenu';
import PlanUsage from './PlanUsage';

import { useChatsSidebar } from '@/hooks/useChatsSidebar';
import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { Separator } from '../ui/Separator';
import { notificationAtom } from './Notification';
import SidebarChatbots from './SidebarChatbots';
import SidebarLinks from './SidebarLinks';
import SidebarSupport from './SidebarSupport';
const PlusMenu = dynamic(() => import('./PlusMenu'), {
  ssr: false,
});

const LeftSidebar = () => {
  const { user, userPlanDetails } = useAuth();
  const { showLeftSidebar, setShowLeftSidebar } = useLeftSidebar();
  const isNotificationOpen = useAtomValue(notificationAtom);

  // Close the chats overlay whenever the main sidebar is hidden.
  const { showChatsSidebar, setShowChatsSidebar } = useChatsSidebar();
  useEffect(() => {
    if (!showLeftSidebar && showChatsSidebar) {
      setShowChatsSidebar(false);
    }
  }, [showLeftSidebar, showChatsSidebar, setShowChatsSidebar]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Press Command + \ (Mac) or Ctrl + \ (Windows/Linux) to toggle sidebar
      if (e.key === '\\' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowLeftSidebar((prev: boolean) => !prev);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const showPlanUsage =
    user &&
    (userPlanDetails?.planName === 'Free' ||
      userPlanDetails?.status !== 'ACTIVE');

  return (
    <div
      className={cn(
        'bg-background fixed sm:relative z-[30] overflow-hidden group/sidebar transition-all duration-200 ease-in-out h-full min-h-0 pt-2 flex flex-col',
        showLeftSidebar
          ? '!flex-shrink-0 sm:flex-[0_1_240px]  max-sm:w-[240px]'
          : 'sm:flex-[0_1_0px] max-sm:w-0 ml-[-1px]',
      )}
    >
      <div className="flex flex-col gap-2.5 min-h-0 h-full px-2">
        <div className="flex items-center">
          <AccountMenu />
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="hover:bg-muted md:group-hover/sidebar:opacity-100 md:opacity-0 transition-all duration-200 ease-in-out"
              onClick={() => {
                setShowLeftSidebar(false);
              }}
            >
              <Icon as={ChevronsLeft} />
            </Button>
            <PlusMenu />
          </div>
        </div>

        <SidebarLinks />
        <SidebarChatbots />
      </div>

      {!showPlanUsage && !isNotificationOpen && (
        <Separator className="bg-borderSecondary" />
      )}
      <div className="flex flex-col p-2 gap-2 flex-shrink-0 z-10">
        {/* <Notification
          variant="loud"
          showClose
          title="Earn $10 when you refer a friend to Anara"
          subtitle="You get cash, they get a $10 off on sign up."
          underlinedText="Learn more"
          onClick={() => {
            setReturnPage(pathname ?? '/new');
            router.push('/settings/referrals');
            track('referral_notification_clicked');
          }}
        /> */}
        {showPlanUsage && <PlanUsage />}
        <SidebarSupport />
      </div>
    </div>
  );
};

export default LeftSidebar;
